import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { FieldType } from 'src/app/settings/model/field-type';
import { Option, ValueField } from 'src/app/settings/model/value-field';
import { EMPTY_VALUE_OPTION_KEY } from '../component/prime-table/parts/column-filter/option-search';
import { Operator } from '../model/operator';
import { RequestCriteria } from '../model/request-criteria';
import { toTime } from '../util/date-util';
import { DataOptionsConnectorService } from './data-options-connector.service';

@Injectable()
export class ColumnFilterService implements OnDestroy {

  private form: FormGroup;
  private allCurrentFiltersSubscription: Subscription;
  private allCurrentFilters: RequestCriteria;
  clearAllFilters$: Subject<null> = new Subject();
  private isClearing = false;
  controlFieldMap: ControlFieldMap = {};

  // Listę wartości dla kolumn z filterOptionsFromData zna tylko tabela (to jej dane), a potrzebują
  // jej kontrolki filtra - i te w nagłówku, i te w panelu "Edytuj filtry kolumn". Liczona jest
  // dopiero przy otwarciu listy, więc przejście po wierszach nie kosztuje przy każdym renderze.
  private dataOptionsProvider: ((columnName: string) => Option[]) | null = null;

  constructor(private dataOptionsConnectorService: DataOptionsConnectorService) {
    this.init();
  }

  ngOnDestroy(): void {
    this.allCurrentFiltersSubscription.unsubscribe();
  }

  registerForm(field: ValueField, name: string, control: AbstractControl): void {
    this.controlFieldMap[name] = {
      type: field.type,
      isSelectable: field.options?.length > 0,
      rawValueFieldName: field.rawValueFieldName
    };
    this.form.addControl(name, control);
  }

  // Bez tego wpis zostaje w controlFieldMap na zawsze po zniknięciu kolumny i createCriteria()
  // odwołuje się do typu pola, którego kontrolka już nie istnieje.
  unregisterForm(name: string): void {
    delete this.controlFieldMap[name];
    if (this.form.contains(name)) this.form.removeControl(name);
  }

  // clearAllFilters$ resetuje synchronicznie wszystkie zarejestrowane formularze kolumnowe -
  // updateForm() wywoływane w trakcie tego resetu nie wysyła jeszcze filtrów (isClearing),
  // dzięki czemu zamiast N zapytań o dane wykonuje się jedno, już po wyzerowaniu wszystkich pól.
  clearAllColumnFilters(): void {
    this.isClearing = true;
    this.dataOptionsConnectorService.clearColumnFilterValues();
    this.clearAllFilters$.next();
    this.isClearing = false;
    this.dataOptionsConnectorService.updateFilters(this.createCriteria());
    this.dataOptionsConnectorService.activateFilters();
  }

  registerDataOptionsProvider(provider: (columnName: string) => Option[]): void {
    this.dataOptionsProvider = provider;
  }

  getDataOptions(columnName: string): Option[] {
    return this.dataOptionsProvider?.(columnName) ?? [];
  }

  getSavedValue(name: string): any {
    return this.dataOptionsConnectorService.getColumnFilterValue(name);
  }

  clearColumnFilter(name: string): void {
    this.form.get(name)?.reset();
  }

  clearColumnFiltersFromService(): void {
    if (!this.allCurrentFilters) return;
    const remaining = (this.allCurrentFilters.criteria ?? []).filter(c => !c.columnFilter);
    this.dataOptionsConnectorService.updateFilters({ operator: this.allCurrentFilters.operator, criteria: remaining });
    this.dataOptionsConnectorService.activateFilters();
  }

  updateForm(name: string, control: AbstractControl): void {
    this.form.setControl(name, control);
    if (this.isClearing) return;
    this.dataOptionsConnectorService.setColumnFilterValue(name, control.value);
    this.dataOptionsConnectorService.updateFilters(this.createCriteria());
    this.dataOptionsConnectorService.activateFilters();
  }

  applyValues(entries: Array<{ name: string; value: any }>): void {
    entries.forEach(({ name, value }) => {
      const control = this.form.get(name);
      if (!control) return;
      control.setValue(value, { emitEvent: false });
      this.dataOptionsConnectorService.setColumnFilterValue(name, value);
    });
    this.dataOptionsConnectorService.updateFilters(this.createCriteria());
    this.dataOptionsConnectorService.activateFilters();
  }

  private init(): void {
    this.allCurrentFiltersSubscription = this.dataOptionsConnectorService.activeFilters$
      .subscribe((filters: RequestCriteria) => this.allCurrentFilters = filters);
    this.form = new FormGroup({});
    this.controlFieldMap = {};
  }

  private createCriteria(): RequestCriteria {
    const columnCriteria: RequestCriteria[] = [];
    // Pusta tablica przechodzi dalej i celowo tworzy kryterium, które nie przepuszcza niczego:
    // w trybie listy "nic nie zaznaczone" to wybór, a brak filtru zapisany jest jako null.
    Object.entries(this.form.value)
      .filter(entry => entry[1] !== null && entry[1] !== undefined && entry[1] !== '')
      .forEach(([fieldName, value]) => {
        // Pola słownikowe (field.options) mają wielokrotny wybór - wiersz pasuje, jeśli zgadza się
        // z którąkolwiek z zaznaczonych wartości.
        if (Array.isArray(value)) {
          columnCriteria.push(ColumnFilterService.createInListCriteria(
            this.controlFieldMap[fieldName]?.rawValueFieldName ?? fieldName, value
          ));
          return;
        }
        columnCriteria.push(this.createValueCriteria(fieldName, value));
      });
    if (this.allCurrentFilters == null) {
      return { operator: Operator.and, criteria: columnCriteria };
    }
    let newCriteria: RequestCriteria[] = this.allCurrentFilters.criteria;
    if (newCriteria.length) {
      const criteriaForDelete = newCriteria.filter((crit: RequestCriteria) => crit.columnFilter);
      criteriaForDelete.forEach((crit: RequestCriteria) => newCriteria.splice(newCriteria.indexOf(crit), 1));
      newCriteria.push(...columnCriteria);
    }
    else
      newCriteria = columnCriteria;
    return { operator: Operator.and, criteria: newCriteria };
  }

  /**
   * Wybór z listy dopasowuje się dokładnie, więc porównanie musi iść po tym samym polu, z którego
   * powstała lista - dla kolumn ozdabiających komórkę po ValueField.rawValueFieldName (nr boczny
   * pojazdu: "5301", a w kolumnie "5301✍️"). Filtr kolumnowy zostaje po stronie klienta, więc nazwa
   * pola spoza kolumn nie ma jak dojść do serwera (patrz excludeColumnFilterCriteria).
   *
   * Pozycja "bez wartości" nie jest wartością z danych - z listy schodzi do osobnej flagi kryterium.
   */
  private static createInListCriteria(fieldName: string, values: any[]): RequestCriteria {
    const includeEmpty = values.includes(EMPTY_VALUE_OPTION_KEY);
    return {
      operator: Operator.inList,
      fieldName,
      values: includeEmpty ? values.filter(value => value !== EMPTY_VALUE_OPTION_KEY) : values,
      includeEmpty,
      columnFilter: true
    };
  }

  private createValueCriteria(fieldName: string, value: unknown): RequestCriteria {
    switch (this.controlFieldMap[fieldName].type) {
      case FieldType.TIME: {
        const timeValues = (value as string).split(':');
        const dateTime = new Date();
        dateTime.setHours(Number.parseInt(timeValues[0]));
        dateTime.setMinutes(Number.parseInt(timeValues[1]));
        dateTime.setSeconds(0);
        return {
          operator: Operator.between,
          fieldName,
          start: toTime(moment(dateTime).subtract(1, 'second'), false),
          end: toTime(moment(dateTime).add(1, 'second'), false),
          columnFilter: true
        };
      }
      case FieldType.DURATION:
        return { operator: Operator.iContainsCustom, fieldName, value: value as string, columnFilter: true };
      case FieldType.FLOAT:
      case FieldType.INTEGER:
        // 'speed' is stored in m/s but displayed in km/h, so it needs the custom conversion in
        // data-util.ts's iContainsCustom rather than a plain substring match against the raw value.
        return fieldName == 'speed'
          ? { operator: Operator.iContainsCustom, fieldName, value: value as string, columnFilter: true }
          : { operator: Operator.iContains, fieldName, value: value as string, columnFilter: true };
      case FieldType.BOOLEAN:
        return { operator: Operator.equals, fieldName, value: value as string, columnFilter: true };
      default:
        return {
          operator: this.controlFieldMap[fieldName].isSelectable ? Operator.equals : Operator.iContains,
          fieldName,
          value: value as string,
          columnFilter: true
        };
    }
  }
}

interface ControlFieldMap {
  [key: string]: { type: FieldType; isSelectable: boolean; rawValueFieldName?: string };
}
